/**
 * SuiteTabs · Foundation Component (BR-FND-001)
 *
 * Unico componente Tabs autorizzato dell'ecosistema Gruppo Impresa.
 * Wrapper del primitive Radix (@radix-ui/react-tabs) con contratto
 * permanente di non-sovrapposizione delle etichette.
 *
 * Business Rule certificata: BR-FND-001 "Standard Tabs antioverflow"
 * (`memory/BUSINESS_RULES_CERTIFICATE.md`).
 *
 * Caratteristiche permanenti:
 *   - rilevamento automatico overflow via ResizeObserver;
 *   - fallback automatico: scroll orizzontale con chevron e fade,
 *     overflow menu su soglia, layout verticale esplicito;
 *   - API retrocompatibile con shadcn Tabs (compound components);
 *   - estensibile a: badge, icon, tooltip, dirty state, status,
 *     complete indicator, lock, lazy content, visibility (hidden).
 *
 * Qualunque modulo della Suite deve usare ESCLUSIVAMENTE questo
 * componente, non direttamente Radix o shadcn/ui/tabs.
 */
import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import {
  ChevronLeft, ChevronRight, MoreHorizontal, AlertCircle,
  AlertTriangle, CheckCircle2, Lock, Circle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

// ---------------------------------------------------------------------------
// Context: condivide orientamento e lock-navigation fra List e Trigger
// ---------------------------------------------------------------------------
const SuiteTabsCtx = React.createContext({
  orientation: "horizontal",
  lockedValue: null,
});

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------
const SuiteTabs = React.forwardRef(function SuiteTabs(
  { orientation = "horizontal", lockedValue = null, onValueChange, className, ...props },
  ref,
) {
  const handleChange = React.useCallback(
    (next) => {
      // BR-FND-001 · lock di navigazione: se un tab e' lockedValue
      // la navigazione non lo abbandona (il consumer puo' sbloccarlo).
      if (lockedValue && props.value && props.value !== next && props.value === lockedValue) {
        return;
      }
      onValueChange?.(next);
    },
    [onValueChange, lockedValue, props.value],
  );
  return (
    <SuiteTabsCtx.Provider value={{ orientation, lockedValue }}>
      <TabsPrimitive.Root
        ref={ref}
        orientation={orientation}
        onValueChange={handleChange}
        className={cn(
          "min-w-0 max-w-full",
          orientation === "vertical" && "flex gap-4",
          className,
        )}
        {...props}
      />
    </SuiteTabsCtx.Provider>
  );
});

// ---------------------------------------------------------------------------
// List: contenitore delle tabs con auto-overflow (scroll/menu/vertical)
// ---------------------------------------------------------------------------
const SuiteTabsList = React.forwardRef(function SuiteTabsList(
  { className, children, overflowMode = "auto", menuThreshold = 7, ...props },
  ref,
) {
  const { orientation } = React.useContext(SuiteTabsCtx);

  // Vertical: lista verticale, nessuna logica di overflow (BR-FND-001
  // §layout verticale previsto dal Design System).
  if (orientation === "vertical") {
    return (
      <TabsPrimitive.List
        ref={ref}
        className={cn(
          "flex flex-col gap-1 min-w-[180px] p-1 rounded-lg bg-muted text-muted-foreground",
          className,
        )}
        {...props}
      >
        {children}
      </TabsPrimitive.List>
    );
  }

  const effectiveMode = overflowMode === "auto"
    ? (React.Children.count(children) > menuThreshold ? "menu" : "scroll")
    : overflowMode;

  if (effectiveMode === "menu") {
    return (
      <OverflowMenuList ref={ref} className={className} {...props}>
        {children}
      </OverflowMenuList>
    );
  }
  return (
    <OverflowScrollList ref={ref} className={className} {...props}>
      {children}
    </OverflowScrollList>
  );
});

// ---------------------------------------------------------------------------
// Variante SCROLL: scroll orizzontale con chevron laterali e fade
// ---------------------------------------------------------------------------
const OverflowScrollList = React.forwardRef(function OverflowScrollList(
  { className, children, ...props }, outerRef,
) {
  const scrollerRef = React.useRef(null);
  const [canL, setCanL] = React.useState(false);
  const [canR, setCanR] = React.useState(false);

  const recompute = React.useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanL(scrollLeft > 1);
    setCanR(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  React.useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    // Doppia misurazione: la prima sincrona post-commit, la seconda in
    // requestAnimationFrame per garantire layout stabilizzato (necessario
    // dentro dialog con animazioni di apertura).
    recompute();
    const raf = requestAnimationFrame(recompute);
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    el.addEventListener("scroll", recompute, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      el.removeEventListener("scroll", recompute);
    };
  }, [recompute]);

  // Auto-scroll sul tab attivo quando entra lo stato active
  React.useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const active = el.querySelector('[data-state="active"]');
    if (active && typeof active.scrollIntoView === "function") {
      active.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
    }
  });

  const scrollBy = (dx) => {
    scrollerRef.current?.scrollBy({ left: dx, behavior: "smooth" });
  };

  return (
    <div ref={outerRef} className={cn("relative w-full max-w-full min-w-0 overflow-hidden", className)}>
      {canL && (
        <button
          type="button"
          aria-label="Scorri a sinistra"
          data-testid="suite-tabs-scroll-left"
          onClick={() => scrollBy(-180)}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-7 w-7 inline-flex items-center justify-center rounded-full bg-background shadow border border-border hover:bg-muted"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      {canR && (
        <button
          type="button"
          aria-label="Scorri a destra"
          data-testid="suite-tabs-scroll-right"
          onClick={() => scrollBy(180)}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-7 w-7 inline-flex items-center justify-center rounded-full bg-background shadow border border-border hover:bg-muted"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
      {canL && (
        <div className="pointer-events-none absolute left-0 top-0 h-full w-8 bg-gradient-to-r from-background to-transparent z-[1]" />
      )}
      {canR && (
        <div className="pointer-events-none absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-background to-transparent z-[1]" />
      )}
      <TabsPrimitive.List
        ref={scrollerRef}
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        className={cn(
          "flex items-center gap-1 overflow-x-auto rounded-lg bg-muted p-1 text-muted-foreground [&::-webkit-scrollbar]:hidden",
          canL && "pl-8", canR && "pr-8",
        )}
        {...props}
      >
        {children}
      </TabsPrimitive.List>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Variante MENU: tab visibili + overflow in DropdownMenu
// ---------------------------------------------------------------------------
const OverflowMenuList = React.forwardRef(function OverflowMenuList(
  { className, children, ...props }, outerRef,
) {
  const listRef = React.useRef(null);
  const [overflowValues, setOverflowValues] = React.useState([]);
  const kids = React.Children.toArray(children);

  const recompute = React.useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const items = Array.from(list.querySelectorAll("[data-suite-tab-value]"));
    const container = list.getBoundingClientRect();
    const hidden = [];
    items.forEach((it) => {
      const r = it.getBoundingClientRect();
      if (r.right > container.right - 44 /* spazio bottone menu */) {
        hidden.push(it.getAttribute("data-suite-tab-value"));
      }
    });
    setOverflowValues(hidden);
  }, []);

  React.useEffect(() => {
    recompute();
    const ro = new ResizeObserver(recompute);
    if (listRef.current) ro.observe(listRef.current);
    return () => ro.disconnect();
  }, [recompute, kids.length]);

  return (
    <div ref={outerRef} className={cn("relative w-full max-w-full min-w-0 overflow-hidden", className)}>
      <div className="flex items-center gap-1 rounded-lg bg-muted p-1 text-muted-foreground">
        <TabsPrimitive.List
          ref={listRef}
          className="flex items-center gap-1 flex-1 min-w-0 overflow-hidden"
          {...props}
        >
          {kids.map((c) =>
            React.isValidElement(c)
              ? React.cloneElement(c, {
                  // nascondo visivamente ma mantengo nel DOM per RadixTabs
                  style: overflowValues.includes(c.props.value) ? { visibility: "hidden", position: "absolute", pointerEvents: "none" } : undefined,
                })
              : c,
          )}
        </TabsPrimitive.List>
        {overflowValues.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`${overflowValues.length} schede aggiuntive`}
                data-testid="suite-tabs-overflow-menu"
                className="h-7 min-w-[32px] inline-flex items-center justify-center rounded-md hover:bg-background text-foreground gap-1 px-1"
              >
                <MoreHorizontal className="h-4 w-4" />
                <span className="text-xs font-medium">{overflowValues.length}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {kids
                .filter((c) => React.isValidElement(c) && overflowValues.includes(c.props.value))
                .map((c) => (
                  <OverflowMenuItemFromTab key={c.props.value} tab={c} />
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
});

const OverflowMenuItemFromTab = ({ tab }) => {
  // Trigger "virtuale" che riusa il value del tab per attivarlo via Radix
  return (
    <TabsPrimitive.Trigger value={tab.props.value} asChild>
      <DropdownMenuItem data-testid={`suite-tabs-overflow-item-${tab.props.value}`}>
        {tab.props.icon ? <span className="mr-2">{tab.props.icon}</span> : null}
        <span className="flex-1">{tab.props.children ?? tab.props.label}</span>
        {tab.props.badge != null && (
          <span className="ml-2 text-xs rounded-full bg-primary/10 text-primary px-1.5 py-0.5">
            {tab.props.badge}
          </span>
        )}
      </DropdownMenuItem>
    </TabsPrimitive.Trigger>
  );
};

// ---------------------------------------------------------------------------
// Tab (trigger)
// Props estese: icon, badge, tooltip, dirty, status, complete, lock, hidden
// ---------------------------------------------------------------------------
const SuiteTab = React.forwardRef(function SuiteTab(
  {
    className, children, value, icon, badge, tooltip,
    dirty = false, status = null, complete = false,
    lock = false, hidden = false, disabled = false,
    ...props
  },
  ref,
) {
  const { orientation } = React.useContext(SuiteTabsCtx);
  if (hidden) return null;

  const statusIcon = status === "error"
    ? <AlertCircle className="h-3.5 w-3.5 text-destructive" aria-label="errore" />
    : status === "warning"
      ? <AlertTriangle className="h-3.5 w-3.5 text-amber-500" aria-label="attenzione" />
      : null;

  const inner = (
    <TabsPrimitive.Trigger
      ref={ref}
      value={value}
      disabled={disabled}
      data-suite-tab-value={value}
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow gap-1.5 shrink-0",
        orientation === "vertical" && "w-full justify-start",
        className,
      )}
      {...props}
    >
      {icon && <span className="shrink-0 flex items-center">{icon}</span>}
      <span>{children}</span>
      {complete && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" aria-label="completata" />}
      {statusIcon}
      {dirty && (
        <Circle
          className="h-2 w-2 fill-current text-primary shrink-0"
          aria-label="modifiche non salvate"
          data-testid={`suite-tab-dirty-${value}`}
        />
      )}
      {badge != null && (
        <span className="ml-0.5 text-[10px] font-semibold leading-none rounded-full bg-primary/10 text-primary px-1.5 py-0.5 shrink-0">
          {badge}
        </span>
      )}
      {lock && <Lock className="h-3 w-3 opacity-60 shrink-0" aria-label="bloccato" />}
    </TabsPrimitive.Trigger>
  );

  if (tooltip) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>{inner}</TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return inner;
});

// ---------------------------------------------------------------------------
// Content: wrapper trasparente di TabsPrimitive.Content (Radix/shadcn).
// Nessuna personalizzazione del comportamento di mount: solo content
// con value === context.value è presente nel DOM (comportamento nativo).
// ---------------------------------------------------------------------------
const SuiteTabsContent = React.forwardRef(function SuiteTabsContent(
  { className, ...props }, ref,
) {
  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn(
        "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 flex-1 min-w-0",
        className,
      )}
      {...props}
    />
  );
});

export { SuiteTabs, SuiteTabsList, SuiteTab, SuiteTabsContent };
